import { Banner } from '@ori/presentation-components';
import useTestId from '@ori/testid-generator';
import type { FC } from 'react';

import { useTranslations } from '../../../hooks/useTranslations';
import {
  CATALOGUES_SEE_ALL,
  CATALOGUES_SEE_ALL_LINK,
  CTA_TEXT,
  DESKTOP_CATALOGUE,
  DETAIL_BUTTON,
  LINK,
  MOBILE_CATALOGUE,
  PRODUCT_IMAGE,
  TITLE,
  VALIDITY,
} from '../../common';
import { ProductImage } from './CatalogueImage';
import {
  MobileWrapper,
  CallToAction,
  CatalogueDetailButton,
  CatalogueLink,
  Centered,
  CTAText,
  CurrentCatalog,
  ImageWrapper,
  Title,
  Validity,
} from './styles';
import type { CurrentCatalogueProps } from './types';
import { CatalogueType } from './types';

export const CurrentCatalogue: FC<CurrentCatalogueProps> = (props) => {
  const { formatMessage } = useTranslations();
  const { getTestId } = useTestId();

  const {
    onClick,
    isMobile,
    catalogue: { url, title, imageUrl, validity },
    linkToAllCatalogues,
  } = props;

  if (isMobile) {
    return (
      <MobileWrapper data-testid={getTestId(MOBILE_CATALOGUE)}>
        <Banner
          type="catalogue"
          dateRange={validity ?? ''}
          title={title ?? ''}
          imageUrl={imageUrl}
          url={url}
        />
        {linkToAllCatalogues ? (
          <CatalogueLink
            hoverEffect
            data-testid={getTestId(LINK)}
            href={linkToAllCatalogues}
          >
            <CTAText
              isMobile
              data-testid={getTestId(CTA_TEXT)}
            >
              {formatMessage('seeAll')}
            </CTAText>
          </CatalogueLink>
        ) : null}
      </MobileWrapper>
    );
  }

  return (
    <CurrentCatalog
      data-testid={getTestId(DESKTOP_CATALOGUE)}
      onClick={onClick}
    >
      <CatalogueLink
        data-testid={getTestId(LINK)}
        href={url}
      >
        <ImageWrapper catalogue={CatalogueType.Current}>
          <ProductImage
            alt={title ?? ''}
            image={imageUrl ?? ''}
            catalogue={CatalogueType.Current}
            data-testid={getTestId(PRODUCT_IMAGE)}
          />
        </ImageWrapper>
      </CatalogueLink>
      <Centered>
        <CatalogueLink
          hoverEffect={false}
          href={url}
          data-testid={getTestId(LINK)}
        >
          <Title data-testid={getTestId(TITLE)}>{title}</Title>
        </CatalogueLink>
        <Validity data-testid={getTestId(VALIDITY)}>{validity}</Validity>
        <CatalogueDetailButton
          data-testid={getTestId(DETAIL_BUTTON)}
          variant="outlined"
          href={url}
        >
          {formatMessage('view')}
        </CatalogueDetailButton>
      </Centered>

      {linkToAllCatalogues ? (
        <CallToAction data-testid={getTestId(CATALOGUES_SEE_ALL)}>
          <CTAText isMobile={false}>{formatMessage('callToActionCatalogueText')}</CTAText>
          <CatalogueDetailButton
            fullWidth
            variant="outlined"
            data-testid={getTestId(CATALOGUES_SEE_ALL_LINK)}
            href={linkToAllCatalogues}
          >
            {formatMessage('seeAllCatalogues')}
          </CatalogueDetailButton>
        </CallToAction>
      ) : null}
    </CurrentCatalog>
  );
};
